/**
 * PAGE: LISTE CONTACTS (VERSION REFACTORISÉE)
 */

import type { ColumnDef } from '@tanstack/react-table';
import { MailWarning, Tag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BulkAssignUserModal,
  BulkCreateTaskModal,
  ColumnSelectorModal,
  CreateContactModal,
  FiltersModal,
  ImportCSVModal,
  SendBulkEmailModal,
} from '../components/modals';
import { ActionButtons, PageContent, PageHeader, PageLayout, Pagination } from '../components/page';
import { ActiveFilterChips, SavedSearchToolbar } from '../components/search';
import { TableCell } from '../components/table';
import { EnterpriseTable, SelectAllBanner } from '../components/tables';
import { ActionsCell, BadgeListCell, CheckboxCell, EmptyCell } from '../components/tables/cells';
import { Badge, BulkActionsBar, ConfirmDialog, ErrorState, toast } from '../components/ui';
import { CONTACT_CONFIG } from '../config/entities/contact.config';
import { useUsers } from '../contexts/UsersContext';
import { useContacts } from '../hooks/useContacts';
import { useEntityList } from '../hooks/useEntityList';
import { useOrigins } from '../hooks/useOrigins';
import { usePermissions } from '../hooks/usePermissions';
import { useProducts } from '../hooks/useProducts';
import { useSearchSessionMemory } from '../hooks/useSearchSessionMemory';
import { useServerPagination } from '../hooks/useServerPagination';
import { useSupabase } from '../hooks/useSupabase';
import {
  generateColumnOptions,
  generateCSVColumns,
  generateCSVTransformers,
  generateFilterConfigs,
  generateImportConfig,
  generateInitialColumnVisibility,
} from '../lib/entity-config';
import type { Contact, ContactStatus } from '../types/contact';
import {
  CONTACT_STATUS_COLORS,
  CONTACT_STATUS_LABELS,
  EMAIL_INVALID_REASON_LABELS,
} from '../types/contact';
import { createSavedSearchDefinition, type SavedSearchDefinition } from '../types/saved-search';

export function ContactsListPage() {
  const navigate = useNavigate();
  const supabase = useSupabase();
  const {
    error,
    create,
    remove,
    removeMany,
    refresh,
    fetchPaginated,
    fetchFiltered,
    fetchAllFilteredIds,
    allData,
  } = useContacts({
    mode: 'server',
  });

  // Permissions utilisateur
  const {
    canCreateEntity,
    canDeleteEntity,
    canImportData,
    canExportData,
    canDeleteBulk,
    canManageAssignments,
    canSendEmails,
  } = usePermissions();

  // Charger les données pour les filtres dropdown
  const { origins } = useOrigins();
  const { products } = useProducts();
  const { usersMap } = useUsers();

  // Pagination server-side (gère loading et data)
  const serverPagination = useServerPagination<Contact>(fetchPaginated!, { initialLimit: 25 });
  const contacts: Contact[] = serverPagination.data;
  const loading = serverPagination.loading;

  // r-dash-align : deep-link depuis le graphique « Contacts par statut » du
  // dashboard (clic sur une barre → /contacts?status=pre_registered). On
  // initialise l'onglet de statut sur le param d'URL s'il est valide, pour que
  // le nombre affiché == le nombre de la barre cliquée (zéro écart).
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<ContactStatus | 'all'>(() => {
    const fromUrl = searchParams.get('status');
    return fromUrl && fromUrl in CONTACT_STATUS_LABELS ? (fromUrl as ContactStatus) : 'all';
  });
  const [isBulkEmailModalOpen, setIsBulkEmailModalOpen] = useState(false);
  const [selectedContactsForEmail, setSelectedContactsForEmail] = useState<Contact[]>([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [isBulkCreateTaskModalOpen, setIsBulkCreateTaskModalOpen] = useState(false);
  // État pour la sélection globale (Feature 4.4)
  const [isAllResultsSelected, setIsAllResultsSelected] = useState(false);
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);

  // Générer automatiquement depuis CONTACT_CONFIG + filtres personnalisés
  const filterConfigs = useMemo(() => {
    const baseConfigs = generateFilterConfigs(CONTACT_CONFIG);
    // Remplacer le filtre 'origin' par un filtre dropdown sur 'origin_id'
    const filteredConfigs = baseConfigs.filter(
      (config) => config.field !== 'origin' && config.field !== 'origin_info'
    );
    // R-parite-asso : filtres « Origine » et « Produit » TOUJOURS disponibles,
    // comme « Assigné à » (cf. ci-dessous). On ne conditionne plus la présence du
    // filtre à la disponibilité des options (origins/products), sinon un rôle dont
    // la RLS limite la lecture d'une table de référence verrait le filtre disparaître
    // → parité de la LISTE de filtres cassée. La restriction des DONNÉES reste
    // assurée par la RLS ; un dropdown vide est attendu, pas un filtre manquant.
    filteredConfigs.push({
      field: 'origin_id',
      label: 'Origine',
      type: 'select',
      operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
      options: origins.map((o) => ({ value: o.id, label: o.label })),
    });
    filteredConfigs.push({
      field: 'origin_info',
      label: 'Complément info origine',
      type: 'text',
      operators: ['contains', 'notContains', 'equals', 'notEquals', 'isNotNull', 'isNull'],
    });
    // Filtre Produit (via la vue contacts_filter_view)
    filteredConfigs.push({
      field: 'product_id',
      label: 'Produit',
      type: 'select',
      operators: ['contains', 'isNotNull', 'isNull'],
      options: products.map((p) => ({ value: p.id, label: p.name })),
    });

    // Filtre "Assigné à" — toujours disponible (la restriction est faite par RLS sur les données)
    const userOptions = Object.values(usersMap).map((user) => ({
      value: user.id,
      label: user.full_name || user.email,
    }));
    filteredConfigs.push({
      field: 'assigned_to',
      label: 'Assigné à',
      type: 'select',
      operators: ['equals', 'notEquals', 'isNotNull', 'isNull'],
      options: userOptions,
    });

    // Filtre "Nombre d'appels" sur la colonne agrégée call_count (vue contacts_filter_view)
    filteredConfigs.push({
      field: 'call_count',
      label: "Nombre d'appels",
      type: 'number',
      operators: [
        'equals',
        'notEquals',
        'greaterThan',
        'lessThan',
        'between',
        'isNotNull',
        'isNull',
      ],
    });

    // ✨ Sprint 5 : Filtre "Fait partie d'un équipage"
    filteredConfigs.push({
      field: 'is_in_crew',
      label: "Fait partie d'un équipage",
      type: 'select',
      operators: ['equals'],
      options: [
        { value: 'true', label: 'Oui' },
        { value: 'false', label: 'Non' },
      ],
    });

    return filteredConfigs;
  }, [origins, products, usersMap]);
  const csvColumns = useMemo(() => generateCSVColumns(CONTACT_CONFIG), []);
  const csvTransformers = useMemo(() => generateCSVTransformers(CONTACT_CONFIG), []);
  const importConfig = useMemo(() => generateImportConfig(CONTACT_CONFIG), []);
  const initialColumnVisibility = useMemo(
    () => generateInitialColumnVisibility(CONTACT_CONFIG),
    []
  );

  // ✅ Memoïser les filtres pour éviter de recréer le tableau à chaque render
  const statusFilterConditions = useMemo(() => {
    if (statusFilter === 'all') {
      return [];
    }
    return [
      {
        field: 'status',
        operator: 'equals' as const,
        value: statusFilter,
      },
    ];
  }, [statusFilter]);

  const entity = useEntityList({
    data: contacts, // Données paginées pour affichage
    allData, // Toutes les données pour export/import
    loading,
    error,
    create,
    remove,
    removeMany,
    refresh: async () => {
      const data = await refresh();
      await serverPagination.refresh();
      return data;
    },
    fetchFiltered, // ✅ Pour export CSV des données filtrées
    // Ticket #195 : l'export CSV filtré doit respecter l'onglet de statut,
    // comme la liste et la sélection globale
    requiredFilters: serverPagination.requiredFilters,
    entityName: CONTACT_CONFIG.entityName,
    entityNamePlural: CONTACT_CONFIG.entityNamePlural,
    searchableFields: ['first_name', 'last_name', 'email', 'phone'] as (keyof Contact)[],
    filterConfigs,
    initialColumnVisibility, // ✅ Visibilité initiale des colonnes
    csvFileName: 'contacts.csv',
    csvColumns,
    csvTransformers,
    importConfig,

    // Mode server-side
    mode: 'server',
    total: serverPagination.total,
    totalPages: serverPagination.totalPages,
    currentPage: serverPagination.currentPage,
    itemsPerPage: serverPagination.itemsPerPage,
    onSearchChange: serverPagination.setSearchTerm,
    onPageChange: serverPagination.setCurrentPage,
    onItemsPerPageChange: serverPagination.setItemsPerPage,
    // Note: onFiltersChange n'est pas passé car on combine les filtres d'onglet
    // et les filtres avancés manuellement via l'useEffect ci-dessous
  });

  const { handleDeleteClick } = entity;

  // Extraire les fonctions pour avoir des références stables dans les dépendances
  const { setFilters: setServerFilters, setRequiredFilters: setServerRequiredFilters } =
    serverPagination;

  // ✅ Synchroniser le filtre d'onglet (statut) avec les requiredFilters server-side
  // Ces filtres s'appliquent TOUJOURS en mode AND, indépendamment du mode des filtres avancés
  useEffect(() => {
    setServerRequiredFilters(statusFilterConditions);
  }, [setServerRequiredFilters, statusFilterConditions]);

  // ✅ Synchroniser les filtres avancés (modal) avec les filtres server-side
  useEffect(() => {
    setServerFilters(entity.table.activeFilters, entity.table.filterMode);
  }, [setServerFilters, entity.table.activeFilters, entity.table.filterMode]);

  const currentSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: entity.table.searchTerm,
        filters: entity.table.activeFilters,
        requiredFilters: statusFilterConditions,
        filterMode: entity.table.filterMode,
        sortField: serverPagination.sortField,
        sortDirection: serverPagination.sortDirection,
        columnVisibility: entity.table.columnVisibility,
        pageSize: entity.table.itemsPerPage,
      }),
    [
      entity.table.activeFilters,
      entity.table.columnVisibility,
      entity.table.filterMode,
      entity.table.itemsPerPage,
      entity.table.searchTerm,
      serverPagination.sortDirection,
      serverPagination.sortField,
      statusFilterConditions,
    ]
  );

  const defaultSavedSearchDefinition = useMemo(
    () =>
      createSavedSearchDefinition({
        searchTerm: '',
        filters: [],
        requiredFilters: [],
        filterMode: 'and',
        sortField: null,
        sortDirection: 'desc',
        columnVisibility: initialColumnVisibility,
        pageSize: 25,
      }),
    [initialColumnVisibility]
  );

  function applySavedSearchDefinition(definition: SavedSearchDefinition) {
    const statusFilter = definition.requiredFilters.find(
      (filter) => filter.field === 'status' && filter.operator === 'equals'
    );
    const statusValue = statusFilter?.value;
    setStatusFilter(
      typeof statusValue === 'string' && statusValue in CONTACT_STATUS_LABELS
        ? (statusValue as ContactStatus)
        : 'all'
    );
    entity.table.setSearchTerm(definition.searchTerm);
    entity.table.setActiveFilters(definition.filters, definition.filterMode);
    entity.table.setColumnVisibility(definition.columnVisibility);
    entity.table.setItemsPerPage(definition.pageSize);
    serverPagination.setSort(definition.sort?.field || null, definition.sort?.direction);
    entity.table.clearSelection();
  }

  useSearchSessionMemory({
    entityType: 'contacts',
    currentDefinition: currentSavedSearchDefinition,
    filterConfigs: entity.filterConfigs,
    onRestore: applySavedSearchDefinition,
    skip: searchParams.get('status') !== null,
  });

  const handleBulkEmail = async () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun contact sélectionné');
      return;
    }

    // ✅ En mode server-side pagination, récupérer les données par IDs depuis la base
    const selectedIds = Array.from(entity.table.selectedIds);

    try {
      const { data: selectedContacts, error: fetchError } = await supabase
        .from('contacts')
        .select('*')
        .in('id', selectedIds);

      if (fetchError) {
        console.error('Error fetching contacts for email:', fetchError);
        toast.error('Erreur lors de la récupération des contacts');
        return;
      }

      if (!selectedContacts || selectedContacts.length === 0) {
        toast.error('Impossible de récupérer les contacts sélectionnés');
        return;
      }

      setSelectedContactsForEmail(selectedContacts as Contact[]);
      setIsBulkEmailModalOpen(true);
    } catch (err) {
      console.error('Error in handleBulkEmail:', err);
      toast.error("Erreur lors de la préparation de l'envoi");
    }
  };

  const handleBulkAssign = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun contact sélectionné');
      return;
    }
    setIsBulkAssignModalOpen(true);
  };

  /**
   * Ouvrir la modal de création de relance en masse (item #16)
   */
  const handleBulkCreateTask = () => {
    if (entity.table.selectedCount === 0) {
      toast.error('Aucun contact sélectionné');
      return;
    }
    setIsBulkCreateTaskModalOpen(true);
  };

  /**
   * Assigner un utilisateur à tous les contacts sélectionnés
   */
  const handleBulkAssignUser = async (userId: string) => {
    // ✅ Utiliser selectedIds (global) au lieu de selectedItems (page courante)
    const contactIds = Array.from(entity.table.selectedIds);

    try {
      // Mise à jour en masse via Supabase
      const { error } = await supabase
        .from('contacts')
        .update({ assigned_to: userId })
        .in('id', contactIds);

      if (error) throw error;

      toast.success(`${contactIds.length} contact(s) assigné(s) avec succès`);

      await refresh();
      await serverPagination.refresh();
    } catch (err) {
      console.error("Erreur lors de l'assignation en masse:", err);
      toast.error("Erreur lors de l'assignation");
      throw err;
    }
  };

  /**
   * Sélectionner tous les résultats correspondant aux filtres actuels (Feature 4.4)
   */
  const handleSelectAllResults = async () => {
    if (!fetchAllFilteredIds) return;

    setIsLoadingAllIds(true);
    try {
      const allIds = await fetchAllFilteredIds({
        search: serverPagination.searchTerm,
        filters: serverPagination.filters,
        filterMode: serverPagination.filterMode,
        // Retour réunion 07/07 (D) : le filtre d'onglet statut (prospect / pays…)
        // est appliqué via requiredFilters (cf. setServerRequiredFilters). Sans le
        // transmettre ici, « tout sélectionner » ignorait le sous-ensemble filtré
        // et prenait TOUS les contacts. Le fix de juin n'avait couvert que la page
        // Prospects (pas Contacts/Équipages).
        requiredFilters: serverPagination.requiredFilters,
      });

      // Mettre à jour selectedIds avec tous les IDs
      entity.table.setSelectedIds(new Set(allIds));
      setIsAllResultsSelected(true);
      toast.success(`${allIds.length} ${CONTACT_CONFIG.entityNamePlural} sélectionnés`);
    } catch (err) {
      console.error('Erreur sélection globale:', err);
      toast.error('Erreur lors de la sélection');
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  // Reset de la sélection quand les filtres, la recherche ou l'onglet de
  // statut (requiredFilters) changent. Sans requiredFilters dans les deps, la
  // sélection globale survivait au changement d'onglet → action de masse sur
  // l'ancien périmètre. Ticket #195 : clearSelection() aussi, pas seulement le
  // booléen — sinon les IDs de l'ancien périmètre restaient sélectionnés
  // (barre d'actions « N sélectionnés » sur tableau vierge, export CSV faux).
  const { clearSelection } = entity.table;
  useEffect(() => {
    setIsAllResultsSelected(false);
    clearSelection();
  }, [
    serverPagination.filters,
    serverPagination.searchTerm,
    serverPagination.requiredFilters,
    clearSelection,
  ]);

  const columns: ColumnDef<Contact>[] = useMemo(
    () => [
      {
        id: 'select',
        size: 60,
        header: ({ table }) => (
          <CheckboxCell
            row={
              {
                getIsSelected: () => table.getIsAllRowsSelected(),
                toggleSelected: (value?: boolean) => table.toggleAllRowsSelected(value),
              } as any
            }
          />
        ),
        cell: ({ row }) => <CheckboxCell row={row} />,
      },
      {
        accessorKey: 'last_name',
        header: 'Nom',
        cell: ({ getValue }) => (
          <TableCell className="font-semibold">{getValue() as string}</TableCell>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'first_name',
        header: 'Prénom',
        cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
        enableSorting: true,
      },
      {
        id: 'products',
        header: 'Produits',
        accessorFn: (row) => row.products?.map((p) => p.name).join(', ') || '-',
        cell: ({ row }) => (
          <BadgeListCell
            items={row.original.products || []}
            renderLabel={(product) => product.name}
            getKey={(product) => product.id}
            variant="secondary"
            maxDisplay={5}
          />
        ),
        enableSorting: false,
      },
      // ✨ Sprint 5 : Colonne N° Équipage
      {
        id: 'crew_numbers',
        header: 'N° Équipage',
        accessorFn: (row) => row.crew_numbers?.join(', ') || '-',
        cell: ({ row }) => (
          <BadgeListCell
            items={row.original.crew_numbers || []}
            renderLabel={(crewNumber) => crewNumber}
            getKey={(crewNumber) => crewNumber}
            variant="outline"
            maxDisplay={2}
            emptyMessage="-"
          />
        ),
        enableSorting: false,
        size: 150,
      },
      // Retour réunion 07/07 (E) : colonne « N° BPI » (masquée par défaut).
      // Sans ce ColumnDef, la cocher dans le sélecteur n'affichait rien.
      {
        accessorKey: 'bpi_code',
        header: 'N° BPI',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string} />,
        enableSorting: false,
        size: 140,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-1.5">
            {/* Indicateur délivrabilité : email flaggé invalide par le webhook Mailjet (TICKET-CRM-13) */}
            {row.original.email_invalid_at && (
              <span
                title={`Email non délivré (${EMAIL_INVALID_REASON_LABELS[row.original.email_invalid_reason ?? 'hard_bounce']})`}
                className="flex shrink-0 items-center"
              >
                <MailWarning className="h-3.5 w-3.5 text-red-600" aria-label="Email invalide" />
              </span>
            )}
            <TableCell className="min-w-0">{getValue() as string}</TableCell>
          </div>
        ),
        enableSorting: true,
        size: 200,
      },
      {
        accessorKey: 'phone',
        header: 'Téléphone',
        cell: ({ getValue }) => <EmptyCell value={getValue() as string} />,
        enableSorting: true,
      },
      {
        accessorKey: 'call_count',
        header: "Nb d'appels",
        cell: ({ getValue }) => {
          const count = getValue() as number;
          return <TableCell className="text-zinc-600 text-center">{count || 0}</TableCell>;
        },
        enableSorting: true,
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ row }) => {
          const status = row.original.status;
          const colors = CONTACT_STATUS_COLORS[status] ?? {
            bg: 'bg-zinc-50',
            text: 'text-zinc-600',
            border: 'border-zinc-200',
          };
          const label = CONTACT_STATUS_LABELS[status] ?? status;
          return (
            <Badge
              className={`flex items-center gap-1 ${colors.bg} ${colors.text} border ${colors.border}`}
            >
              <Tag className="h-3 w-3" />
              {label}
            </Badge>
          );
        },
        enableSorting: true,
        size: 180,
      },
      // Colonnes additionnelles (masquées par défaut, disponibles dans le sélecteur)
      // Colonnes additionnelles (masquées par défaut, disponibles dans le sélecteur)
      {
        accessorKey: 'assigned_user',
        header: 'Assigné à',
        cell: ({ row }) => (
          <EmptyCell
            value={row.original.assigned_user?.full_name || row.original.assigned_user?.email}
          />
        ),
        // Relation enrichie cote client : pas une colonne de contacts_filter_view.
        // Le tri serveur sur 'assigned_user' renverrait un 400 (cf. sort-columns).
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: 'address',
        header: 'Adresse',
        cell: ({ row }) => <EmptyCell value={row.original.address} />,
        size: 200,
      },
      {
        accessorKey: 'city',
        header: 'Ville',
        cell: ({ row }) => <EmptyCell value={row.original.city} />,
        size: 120,
      },
      {
        accessorKey: 'postal_code',
        header: 'Code postal',
        cell: ({ row }) => <EmptyCell value={row.original.postal_code} />,
        size: 100,
      },
      {
        accessorKey: 'country',
        header: 'Pays',
        cell: ({ row }) => <EmptyCell value={row.original.country} />,
        size: 100,
      },
      {
        accessorKey: 'is_not_student',
        header: 'Plus étudiant',
        cell: ({ row }) => <TableCell>{row.original.is_not_student ? 'Oui' : 'Non'}</TableCell>,
        size: 100,
      },
      {
        accessorKey: 'socio_professional_category',
        header: 'Catégorie socio-pro',
        cell: ({ row }) => <EmptyCell value={row.original.socio_professional_category} />,
        size: 150,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ row }) => (
          <div className="px-3 py-2 max-w-48 truncate text-sm" title={row.original.notes || ''}>
            {row.original.notes || '-'}
          </div>
        ),
        size: 200,
      },
      {
        accessorKey: 'origin',
        header: 'Origine',
        cell: ({ row }) => {
          const originLabel = row.original.origin?.label;
          return originLabel ? (
            <div className="flex items-center">
              <Badge variant="secondary">{originLabel}</Badge>
            </div>
          ) : (
            <span className="text-sm text-zinc-500">-</span>
          );
        },
        enableSorting: false, // Le tri sur relation nécessite une config spéciale côté serveur
        size: 150,
      },
      {
        accessorKey: 'origin_info',
        header: 'Complément info origine',
        cell: ({ row }) => <EmptyCell value={row.original.origin_info} />,
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'created_at',
        header: 'Créé le',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          if (!value) return <EmptyCell value={null} />;
          return (
            <TableCell className="text-zinc-600 text-sm">
              {new Date(value).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TableCell>
          );
        },
        enableSorting: true,
        size: 140,
      },
      {
        accessorKey: 'updated_at',
        header: 'Modifié le',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          if (!value) return <EmptyCell value={null} />;
          return (
            <TableCell className="text-zinc-600 text-sm">
              {new Date(value).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TableCell>
          );
        },
        enableSorting: true,
        size: 140,
      },
      {
        accessorKey: 'last_call_at',
        header: 'Dernier appel',
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          if (!value) return <EmptyCell value={null} />;
          return (
            <TableCell className="text-zinc-600 text-sm">
              {new Date(value).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TableCell>
          );
        },
        enableSorting: true,
        size: 140,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => (
          <ActionsCell
            onView={() => navigate(`/contacts/${row.original.id}`)}
            onDelete={() =>
              handleDeleteClick(
                row.original.id,
                `${row.original.first_name} ${row.original.last_name}`
              )
            }
            canDelete={canDeleteEntity('contact')}
          />
        ),
        size: 120,
      },
    ],
    [navigate, handleDeleteClick, canDeleteEntity]
  );

  // Colonnes disponibles pour la sélection (générées automatiquement)
  const columnOptions = useMemo(
    () => generateColumnOptions(CONTACT_CONFIG, entity.table.columnVisibility),
    [entity.table.columnVisibility]
  );

  // ✅ Ne plus conditionner l'affichage sur loading
  // Le tableau affichera son propre indicateur de chargement

  if (entity.error) {
    return (
      <PageLayout>
        <PageHeader breadcrumbs={[{ label: 'Contacts' }]} />
        <div className="w-full h-px bg-zinc-200" />
        <ErrorState
          title="Erreur lors du chargement des contacts"
          message={entity.error.message}
          onRetry={() => window.location.reload()}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader breadcrumbs={[{ label: 'Data' }, { label: 'Contacts' }]} />
      <div className="w-full h-px bg-zinc-200" />

      {/* Onglets de filtrage par statut */}
      <div className="flex gap-2 px-6 py-3 bg-zinc-50 border-b border-zinc-200">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            statusFilter === 'all'
              ? 'bg-white text-zinc-900 font-medium shadow-sm'
              : 'text-zinc-600 hover:text-zinc-900 hover:bg-white/50'
          }`}
        >
          Tous
          {statusFilter === 'all' && serverPagination.total > 0 && (
            <span className="ml-1.5 text-xs opacity-70">({serverPagination.total})</span>
          )}
        </button>
        {Object.entries(CONTACT_STATUS_LABELS).map(([status, label]) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status as ContactStatus)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              statusFilter === status
                ? 'bg-white text-zinc-900 font-medium shadow-sm'
                : 'text-zinc-600 hover:text-zinc-900 hover:bg-white/50'
            }`}
          >
            {label}
            {statusFilter === status && serverPagination.total > 0 && (
              <span className="ml-1.5 text-xs opacity-70">({serverPagination.total})</span>
            )}
          </button>
        ))}
      </div>

      <PageContent
        title="Contacts"
        description="Gérez vos contacts et prospects"
        actions={
          <ActionButtons
            leadingControls={
              <SavedSearchToolbar
                entityType="contacts"
                currentDefinition={currentSavedSearchDefinition}
                defaultDefinition={defaultSavedSearchDefinition}
                filterConfigs={entity.filterConfigs}
                visibleColumnCount={columnOptions.filter((column) => column.visible).length}
                onApply={applySavedSearchDefinition}
              />
            }
            searchValue={entity.table.searchTerm}
            onSearchChange={entity.table.setSearchTerm}
            onDownload={canExportData('contact') ? entity.handleExportCSV : undefined}
            onUpload={
              canImportData('contact') ? () => entity.setIsImportModalOpen(true) : undefined
            }
            onFilter={() => entity.setIsFiltersModalOpen(true)}
            onColumns={() => entity.setIsColumnModalOpen(true)}
            onCreate={
              canCreateEntity('contact') ? () => entity.setIsCreateModalOpen(true) : undefined
            }
            activeFiltersCount={entity.table.activeFilters.length}
          />
        }
        footer={
          <Pagination
            currentPage={entity.table.currentPage}
            totalPages={entity.table.totalPages}
            itemsPerPage={entity.table.itemsPerPage}
            totalItems={entity.table.totalItems}
            displayedItems={entity.table.displayedItemsCount}
            onPageChange={entity.table.setCurrentPage}
            onItemsPerPageChange={entity.table.setItemsPerPage}
            itemsPerPageOptions={[10, 25, 50, 100]}
          />
        }
      >
        <ActiveFilterChips
          filters={entity.table.activeFilters}
          filterConfigs={entity.filterConfigs}
          filterMode={entity.table.filterMode}
          onChange={entity.table.setActiveFilters}
        />

        {/* Bandeau de sélection globale (Feature 4.4) */}
        <SelectAllBanner
          selectedCount={entity.table.selectedCount}
          pageItemCount={contacts.length}
          allPageSelected={entity.table.isAllPageSelected(contacts)}
          totalFilteredCount={serverPagination.total}
          onSelectAll={handleSelectAllResults}
          onClearSelection={() => {
            entity.table.clearSelection();
            setIsAllResultsSelected(false);
          }}
          entityNamePlural={CONTACT_CONFIG.entityNamePlural}
          isAllSelected={isAllResultsSelected}
          isLoading={isLoadingAllIds}
        />

        <EnterpriseTable
          data={entity.table.filteredData}
          columns={columns}
          onRowSelectionChange={entity.table.setSelectedItems}
          columnVisibility={entity.table.columnVisibility}
          onColumnVisibilityChange={entity.table.setColumnVisibility}
          clearSelectionTrigger={entity.table.clearSelectionTrigger}
          pageSize={entity.table.itemsPerPage}
          currentPage={entity.table.currentPage}
          onPageChange={entity.table.setCurrentPage}
          manualPagination={entity.isServerMode}
          loading={entity.loading}
          // ✅ Tri server-side (tri sur l'ensemble du dataset)
          manualSorting={entity.isServerMode}
          onSortChange={serverPagination.setSort}
          activeSortField={serverPagination.sortField}
          activeSortDirection={serverPagination.sortDirection}
          // ✅ Nouveau système de sélection multi-pages
          selectedIds={entity.table.selectedIds}
          onToggleSelection={entity.table.toggleItemSelection}
          onToggleAllPage={entity.table.toggleAllPageSelection}
        />
      </PageContent>

      <ColumnSelectorModal
        isOpen={entity.isColumnModalOpen}
        onClose={() => entity.setIsColumnModalOpen(false)}
        columns={columnOptions}
        onApply={(newColumns) => {
          const newVisibility: Record<string, boolean> = {};
          newColumns.forEach((col) => {
            newVisibility[col.id] = col.visible !== false;
          });
          entity.table.setColumnVisibility(newVisibility);
        }}
      />

      <FiltersModal
        isOpen={entity.isFiltersModalOpen}
        onClose={() => entity.setIsFiltersModalOpen(false)}
        filterConfigs={entity.filterConfigs}
        activeFilters={entity.table.activeFilters}
        activeFilterMode={entity.table.filterMode}
        onApply={entity.table.setActiveFilters}
      />

      {/* ✅ OPTIMISATION: Monter la modale uniquement quand elle est ouverte */}
      {entity.isCreateModalOpen && (
        <CreateContactModal
          isOpen={entity.isCreateModalOpen}
          onClose={() => entity.setIsCreateModalOpen(false)}
          onSubmit={async (data) => {
            // Dédup email AVANT insert : message clair "contact existant" et
            // modale gardée ouverte. Le garde DB (00139) reste le backstop si
            // allData est partiel ou en cas de course.
            const normEmail = data.email?.trim().toLowerCase();
            const existing = normEmail
              ? allData.find((c) => (c.email ?? '').trim().toLowerCase() === normEmail)
              : undefined;
            if (existing) {
              toast.error(
                `Un contact existe déjà avec cet email : ${existing.first_name} ${existing.last_name}.`
              );
              return false;
            }
            return await entity.handleCreate(data);
          }}
        />
      )}

      <ConfirmDialog
        open={entity.deleteConfirm.open}
        onOpenChange={(open) => entity.setDeleteConfirm({ open })}
        title={
          entity.deleteConfirm.isBulk
            ? `Supprimer les ${CONTACT_CONFIG.entityNamePlural}`
            : `Supprimer le ${CONTACT_CONFIG.entityName}`
        }
        description={
          entity.deleteConfirm.isBulk
            ? `Êtes-vous sûr de vouloir supprimer ${entity.table.selectedCount} ${CONTACT_CONFIG.entityName}(s) ?`
            : `Êtes-vous sûr de vouloir supprimer "${entity.deleteConfirm.name}" ?`
        }
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={entity.confirmDelete}
      />

      <ImportCSVModal
        open={entity.isImportModalOpen}
        onOpenChange={entity.setIsImportModalOpen}
        config={importConfig}
        mode={entity.importMode}
        onCreate={async (data) => {
          const { data: contact, error: supabaseError } = await supabase
            .from('contacts')
            .insert([data as any])
            .select()
            .single();

          if (supabaseError) {
            throw supabaseError;
          }

          return contact;
        }}
        onUpdate={async (id, data) => {
          const result = await supabase
            .from('contacts')
            .update(data as any)
            .eq('id', id);
          if (result.error) throw result.error;
          return true;
        }}
        existingData={allData}
        entityName={CONTACT_CONFIG.entityName}
        entityNamePlural={CONTACT_CONFIG.entityNamePlural}
        onSuccess={entity.handleImportSuccess}
      />

      <SendBulkEmailModal
        isOpen={isBulkEmailModalOpen}
        onClose={() => {
          setIsBulkEmailModalOpen(false);
          setSelectedContactsForEmail([]);
        }}
        entities={selectedContactsForEmail}
        entityType="contact"
        onSuccess={() => {
          refresh();
          serverPagination.refresh();
          setSelectedContactsForEmail([]);
        }}
      />

      {/* Barre d'actions de masse */}
      {entity.table.selectedCount > 0 && (
        <BulkActionsBar
          count={entity.table.selectedCount}
          entityNamePlural={CONTACT_CONFIG.entityNamePlural}
          onDelete={entity.handleBulkDeleteClick}
          onExportCSV={entity.handleExportSelectedCSV}
          onEmail={handleBulkEmail}
          onAssign={handleBulkAssign}
          onCreateTask={handleBulkCreateTask}
          onClearSelection={entity.table.clearSelection}
          showEmailAction={canSendEmails()}
          showAssignAction={canManageAssignments()}
          showCreateTaskAction={true}
          canDelete={canDeleteBulk('contact')}
          canExport={canExportData('contact')}
        />
      )}

      {/* Modale d'assignation en masse */}
      {isBulkAssignModalOpen && (
        <BulkAssignUserModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => setIsBulkAssignModalOpen(false)}
          count={entity.table.selectedCount}
          entityNamePlural={CONTACT_CONFIG.entityNamePlural}
          onAssign={handleBulkAssignUser}
        />
      )}

      {/* Modale de création de relance en masse (item #16) */}
      {isBulkCreateTaskModalOpen && (
        <BulkCreateTaskModal
          isOpen={isBulkCreateTaskModalOpen}
          onClose={() => setIsBulkCreateTaskModalOpen(false)}
          entityType="contact"
          entityIds={Array.from(entity.table.selectedIds)}
          entityNamePlural={CONTACT_CONFIG.entityNamePlural}
          onSuccess={() => {}}
        />
      )}
    </PageLayout>
  );
}
